import { 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  collection, 
  query, 
  where, 
  updateDoc, 
  deleteDoc, 
  addDoc, 
  orderBy, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase';

// Trip Services
export const createTrip = async (tripData) => {
  try {
    console.log('🔥 Creating trip in Firestore:', tripData);
    
    const docRef = await addDoc(collection(db, 'trips'), {
      ...tripData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('✅ Trip created with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating trip:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
};

export const getTrips = async (userId) => {
  try {
    // Try optimized query first (requires composite index)
    const q = query(
      collection(db, 'trips'),
      where('members', 'array-contains', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const memberTrips = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Also try to get trips where user is the creator but not in members
    const creatorQuery = query(
      collection(db, 'trips'),
      where('createdBy', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const creatorSnapshot = await getDocs(creatorQuery);
    const creatorTrips = creatorSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Combine and remove duplicates
    const allTrips = [...memberTrips, ...creatorTrips].filter((trip, index, arr) => 
      arr.findIndex(t => t.id === trip.id) === index
    );
    
    // Calculate totalSpent for each trip by fetching expenses
    const tripsWithExpenses = await Promise.all(allTrips.map(async (trip) => {
      try {
        const expensesQuery = query(
          collection(db, 'expenses'),
          where('tripId', '==', trip.id)
        );
        const expensesSnapshot = await getDocs(expensesQuery);
        const expenses = expensesSnapshot.docs.map(doc => doc.data());
        
        // Calculate total spent from expenses
        const totalSpent = expenses.reduce((sum, expense) => {
          const amount = Number(expense.amount) || 0;
          return sum + amount;
        }, 0);
        
        return {
          ...trip,
          totalSpent
        };
      } catch (error) {
        console.error(`Error fetching expenses for trip ${trip.id}:`, error);
        return {
          ...trip,
          totalSpent: 0
        };
      }
    }));
    
    return tripsWithExpenses;
  } catch (error) {
    console.error('Error with optimized query, falling back to simple query:', error);
    
    // Fallback: Get trips without ordering, then sort in JavaScript
    try {
      const q = query(
        collection(db, 'trips'),
        where('members', 'array-contains', userId)
      );
      const querySnapshot = await getDocs(q);
      const memberTrips = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const creatorQuery = query(
        collection(db, 'trips'),
        where('createdBy', '==', userId)
      );
      const creatorSnapshot = await getDocs(creatorQuery);
      const creatorTrips = creatorSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Combine and remove duplicates
      const allTrips = [...memberTrips, ...creatorTrips].filter((trip, index, arr) => 
        arr.findIndex(t => t.id === trip.id) === index
      );
      
      // Sort by createdAt in JavaScript (newest first)
      return allTrips.sort((a, b) => {
        const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
        const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
        return dateB - dateA;
      });
    } catch (fallbackError) {
      console.error('Fallback query also failed:', fallbackError);
      throw fallbackError;
    }
  }
};

export const getUserTrips = async (userId) => {
  try {
    // Try the optimized query first (requires composite index)
    const q = query(
      collection(db, 'trips'),
      where('members', 'array-contains', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error with optimized query, falling back to simple query:', error);
    
    // Fallback: Get trips without ordering, then sort in JavaScript
    try {
      const q = query(
        collection(db, 'trips'),
        where('members', 'array-contains', userId)
      );
      const querySnapshot = await getDocs(q);
      const trips = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt in JavaScript (newest first)
      return trips.sort((a, b) => {
        const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
        const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
        return dateB - dateA;
      });
    } catch (fallbackError) {
      console.error('Fallback query also failed:', fallbackError);
      throw fallbackError;
    }
  }
};

export const getTrip = async (tripId) => {
  try {
    const docRef = doc(db, 'trips', tripId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error('Trip not found');
    }
  } catch (error) {
    console.error('Error getting trip:', error);
    throw error;
  }
};

export const updateTrip = async (tripId, updateData) => {
  try {
    const tripRef = doc(db, 'trips', tripId);
    await updateDoc(tripRef, {
      ...updateData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating trip:', error);
    throw error;
  }
};

export const deleteTrip = async (tripId) => {
  try {
    await deleteDoc(doc(db, 'trips', tripId));
  } catch (error) {
    console.error('Error deleting trip:', error);
    throw error;
  }
};

// Expense Services
export const createExpense = async (expenseData) => {
  try {
    const docRef = await addDoc(collection(db, 'expenses'), {
      ...expenseData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

export const getExpenses = async (tripId) => {
  try {
    const q = query(
      collection(db, 'expenses'),
      where('tripId', '==', tripId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting expenses:', error);
    throw error;
  }
};

export const updateExpense = async (expenseId, updateData) => {
  try {
    const expenseRef = doc(db, 'expenses', expenseId);
    await updateDoc(expenseRef, {
      ...updateData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

export const deleteExpense = async (expenseId) => {
  try {
    await deleteDoc(doc(db, 'expenses', expenseId));
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

// Real-time listeners
export const subscribeToTrips = (userId, callback) => {
  // Try the optimized query first (requires composite index)
  const q = query(
    collection(db, 'trips'),
    where('members', 'array-contains', userId),
    orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const trips = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(trips);
  }, (error) => {
    console.error('Error with optimized query, falling back to simple query:', error);
    
    // Fallback: Simple query without ordering
    const fallbackQ = query(
      collection(db, 'trips'),
      where('members', 'array-contains', userId)
    );
    
    return onSnapshot(fallbackQ, (querySnapshot) => {
      const trips = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt in JavaScript (newest first)
      const sortedTrips = trips.sort((a, b) => {
        const dateA = a.createdAt ? (a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt)) : new Date(0);
        const dateB = b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt)) : new Date(0);
        return dateB - dateA;
      });
      
      callback(sortedTrips);
    }, (fallbackError) => {
      console.error('Fallback query also failed:', fallbackError);
      callback([]);
    });
  });
};

export const subscribeToExpenses = (tripId, callback) => {
  const q = query(
    collection(db, 'expenses'),
    where('tripId', '==', tripId),
    orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const expenses = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(expenses);
  });
};

// User Services
export const createUserProfile = async (userId, userData) => {
  try {
    await setDoc(doc(db, 'users', userId), {
      ...userData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updateData) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...updateData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};
